import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseStatistic, Sport } from '@mtgame/core';
import { getHeaderHeight } from '@utils/layout';
import { getLeaguePlayerRoute, getTeamPageRoute } from '@config/routes';
import { isNumeric } from 'rxjs/internal-compatibility';

export type StatisticPlayerIdentity = 'user' | 'teamUser' | 'tournamentTeamUser' | 'leaguePlayer';
export type StatisticTeamIdentity = 'team' | 'tournamentTeam';

@Component({
  selector: 'mtg-statistic-table',
  templateUrl: './statistic-table.component.html',
  styleUrls: ['./statistic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatisticTableComponent implements OnChanges {
  @Input()
  title: string;
  @Input()
  sport: Sport;
  @Input()
  order: 'asc' | 'desc';
  @Input()
  statisticHeader: { id?: string, title: string, percent?: boolean, data?: { id: string, title: string, percent?: boolean }[] }[];
  @Input()
  statistic: BaseStatistic[];
  @Input()
  identity: StatisticPlayerIdentity | StatisticTeamIdentity;
  @Input()
  subTitle: 'month' | 'winLoses';
  @Input()
  goalie: boolean;
  @Input()
  sort: string;
  @Input()
  pageSize = 1000;
  @Input()
  teamLogoVisible = true;
  @Input()
  checkNewbie = true;
  page = 1;
  sortTitle: string;
  excludeTeamStatisticFields = ['gamesQualifiedForPlayoff'];

  headerHeight = getHeaderHeight();
  visibleStatistic: BaseStatistic[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sort || changes.statistic || changes.pageSize) {
      this.page = 1;
      this.setSort(this.sort);
      this.paginate();
    }
  }

  changeSort(sort: string): void {
    this.page = 1;
    this.setSort(sort);
    this.paginate();
  }

  setSort(field: string): void {
    if (!this.statistic) {
      return;
    }
    if (field === 'userMinutes') {
      this.sort = 'gameTime';
    } else {
      this.sort = field;
    }
    this.sortTitle = field;
    this.statistic = this.statistic.sort((a, b) => {
      if (a.newbie !== b.newbie && this.checkNewbie) {
        return a.newbie ? 1 : -1;
      }
      let aValue = a[this.sort];
      let bValue = b[this.sort];
      if (this.sort === 'rating') {
        aValue = a.leaguePlayer
          ? parseFloat(a.leaguePlayer.rating)
          : a.tournamentTeamUser
            ? parseFloat(a.tournamentTeamUser.leaguePlayer.rating)
            : null;
        bValue = b.leaguePlayer
          ? parseFloat(b.leaguePlayer.rating)
          : b.tournamentTeamUser
            ? parseFloat(b.tournamentTeamUser.leaguePlayer.rating)
            : null;
      }
      aValue = aValue || 0;
      bValue = bValue || 0;
      if (aValue === bValue) {
        if (a.leaguePlayer?.fullName && b.leaguePlayer?.fullName) {
          return a.leaguePlayer.fullName < b.leaguePlayer.fullName ? -1 : 1;
        }
        if (a.tournamentTeamUser?.leaguePlayer?.fullName && b.tournamentTeamUser?.leaguePlayer?.fullName) {
          return a.tournamentTeamUser.leaguePlayer.fullName < b.tournamentTeamUser.leaguePlayer.fullName ? -1 : 1;
        }
        if (a.teamUser?.user?.fullName) {
          return a.teamUser.user.fullName < b.teamUser.user.fullName ? -1 : 1;
        }
      }
      if (this.order === 'asc') {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });
  }

  paginate(): void {
    this.visibleStatistic = this.statistic.slice(0, this.page * this.pageSize);
  }

  getTeamRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  nextPage(): void {
    if (this.page * this.pageSize > this.statistic.length) {
      return;
    }
    this.page++;
    this.paginate();
  }

  trackById(index: number, item: BaseStatistic): number {
    return item.tournamentTeamUser && item.tournamentTeamUser.id ||
      item.leaguePlayer && item.leaguePlayer.id ||
      item.team && item.team.id ||
      item.tournamentTeam && item.tournamentTeam.id;
  }

  getLeaguePlayerRoute(leaguePlayerId: number): string[] {
    return getLeaguePlayerRoute(leaguePlayerId);
  }

  get nextPageVisible(): boolean {
    if (!this.statistic) {
      return false;
    }
    return this.page * this.pageSize < this.statistic.length;
  }

  floatValue(value: number): string {
    if (!isNumeric(value)) {
      return value;
    }
    return value % 1 !== 0 ? value.toFixed(1) : value.toString();
  }

  isTeamFieldExcluded(field: string): boolean {
    return ['team', 'tournamentTeam'].indexOf(this.identity) > -1 && this.excludeTeamStatisticFields.includes(field);
  }
}
